import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type AxiosError } from 'axios'
import { onboardingApi } from '@/lib/api'
import type {
  OnboardingBarber,
  OnboardingFeatureConfig,
  OnboardingService,
  OnboardingSubmission,
} from '@/lib/api'
import { useToast } from '@/hooks/ui/use-toast'
import { onboardingKeys } from './use-onboarding-queries'

type ApiError = AxiosError<{ detail?: string; message?: string }>

export function useCreateSubmission() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: { shop_name: string }) => {
      const response = await onboardingApi.createSubmission(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submissions })
      toast({ title: 'Success!', description: 'Barbershop submission created with defaults.' })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create submission',
      })
    },
  })
}

export function useUpdateSubmission(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: Partial<OnboardingSubmission>) => {
      const response = await onboardingApi.updateSubmission(submissionId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
      toast({ title: 'Saved', description: 'Changes saved.' })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save',
      })
    },
  })
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (submissionId: number) => {
      await onboardingApi.deleteSubmission(submissionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submissions })
      toast({ title: 'Deleted', description: 'Submission deleted.' })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete',
      })
    },
  })
}

export function useSubmitSubmission(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await onboardingApi.submitSubmission(submissionId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
      toast({
        title: 'Submitted!',
        description: 'Your barbershop configuration is ready for review.',
      })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Cannot Submit',
        description: error.response?.data?.detail || 'Submission incomplete',
      })
    },
  })
}

export function useRevertSubmission(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await onboardingApi.revertSubmission(submissionId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
      toast({
        title: 'Reverted',
        description: 'Back to draft for editing.',
      })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to revert submission',
      })
    },
  })
}

export function useCreateBarber(submissionId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<OnboardingBarber>) => {
      const response = await onboardingApi.createBarber(submissionId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.barbers(submissionId) })
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
    },
  })
}

export function useUpdateBarber(submissionId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      barberId,
      data,
    }: {
      barberId: number
      data: Partial<OnboardingBarber>
    }) => {
      const response = await onboardingApi.updateBarber(submissionId, barberId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.barbers(submissionId) })
    },
  })
}

export function useDeleteBarber(submissionId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (barberId: number) => {
      await onboardingApi.deleteBarber(submissionId, barberId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.barbers(submissionId) })
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
    },
  })
}

export function useCreateService(submissionId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<OnboardingService>) => {
      const response = await onboardingApi.createService(submissionId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.services(submissionId) })
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
    },
  })
}

export function useUpdateService(submissionId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      serviceId,
      data,
    }: {
      serviceId: number
      data: Partial<OnboardingService>
    }) => {
      const response = await onboardingApi.updateService(submissionId, serviceId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.services(submissionId) })
    },
  })
}

export function useDeleteService(submissionId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (serviceId: number) => {
      await onboardingApi.deleteService(submissionId, serviceId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.services(submissionId) })
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
    },
  })
}

export function useCreatePreview(submissionId: number) {
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await onboardingApi.createPreview(submissionId)
      return response.data
    },
    onSuccess: (data) => {
      toast({ title: 'Preview Ready!', description: `Your barbershop is live at ${data.domain}` })
    },
    onError: (error: any) => {
      toast({
        variant: 'destructive',
        title: 'Preview Failed',
        description: error.response?.data?.detail || 'Could not create preview',
      })
    },
  })
}

export function usePaySubmission(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await onboardingApi.paySubmission(submissionId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.submission(submissionId),
      })
      toast({
        title: 'Payment Received!',
        description: 'Your barbershop is ready to deploy.',
      })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Payment Failed',
        description: error.response?.data?.detail || 'Payment failed',
      })
    },
  })
}

export function useDeploySubmission(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await onboardingApi.deploySubmission(submissionId)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.submission(submissionId),
      })
      toast({
        title: 'Deployed!',
        description: `Live at ${data.deployed_domain}`,
      })
    },
    onError: (error: ApiError) => {
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.submission(submissionId),
      })
      toast({
        variant: 'destructive',
        title: 'Deployment Failed',
        description: error.response?.data?.detail || 'Deployment failed. You can retry.',
      })
    },
  })
}

export function useRetryDeploy(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await onboardingApi.retryDeploy(submissionId)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.submission(submissionId),
      })
      toast({
        title: 'Deployed!',
        description: `Live at ${data.deployed_domain}`,
      })
    },
    onError: (error: ApiError) => {
      queryClient.invalidateQueries({
        queryKey: onboardingKeys.submission(submissionId),
      })
      toast({
        variant: 'destructive',
        title: 'Retry Failed',
        description: error.response?.data?.detail || 'Still failing. Try again later.',
      })
    },
  })
}

export function useUpdateFeatures(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: Partial<OnboardingFeatureConfig>) => {
      const response = await onboardingApi.updateFeatures(submissionId, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.features(submissionId) })
      toast({ title: 'Saved', description: 'Feature settings updated.' })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update features',
      })
    },
  })
}

export function useUploadLogo(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await onboardingApi.uploadLogo(submissionId, file)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.submission(submissionId) })
      toast({ title: 'Success', description: 'Logo uploaded successfully.' })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload logo',
      })
    },
  })
}

export function useUploadBarberPhoto(submissionId: number) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ barberId, file }: { barberId: number; file: File }) => {
      const response = await onboardingApi.uploadBarberPhoto(submissionId, barberId, file)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.barbers(submissionId) })
      toast({ title: 'Success', description: 'Photo uploaded successfully.' })
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Upload Failed',
        description: error.response?.data?.detail || 'Failed to upload photo',
      })
    },
  })
}
